import { useEffect, useState } from 'react'
import { editUnitFormDefault } from './consts'
import { EditUnitFormState } from '../model/types'
import { FormikConfig } from 'formik'
import { notification } from '@/shared/helpers/notificationMessages'
import {
  createAutocompleteOptions,
  createTranslatedSelectOptions,
  getCurrentTranslation,
  hasAiTranslation,
  matchTranslations,
} from '@/shared/lib/utils'
import { useEditUnitApi } from '../api/edit-unit-form-api'
import { errorHandler } from '@/shared/lib/errorHandler'
import { useUnitAmenitiesByPage, useUnitById } from '@/entities/Unit'
import { useTranslation } from 'react-i18next'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { updateTranslationsForEditing } from '@/shared/lib/updateTranslationsForEditing'
import { useDebounce } from '@/shared/lib/useDebounce'
import { useSearchBuildingsByText } from '@/entities/Building/api/building-api'
import { EditTranslations } from '@/entities/ContentTranslation/model/types'
import { DeletePriceBody } from '@/shared/api/topli/TopliApi'
import { transformPricesFromServer } from '@/shared/helpers/priceHelpers'
import { hasTranslationsChanged } from '@/entities/ContentTranslation'

export const useEditUnitForm = (currentUnitId: number, onCloseModal: () => void) => {
  const { language } = useLanguageStore()
  const { data: amenities, isLoading: isLoadingAmenities } = useUnitAmenitiesByPage(
    1000,
    0,
    language,
  )
  const [searchBuilding, setSearchBuilding] = useState<string>('')
  const [initialValues, setInitialValues] = useState<EditUnitFormState>(() => editUnitFormDefault)
  const [deletedPrices, setDeletedPrices] = useState<DeletePriceBody[]>([])
  const [originalTranslations, setOriginalTranslations] = useState<EditTranslations | null>(null)
  const { t } = useTranslation(['common'])

  const {
    data: currentUnit,
    isLoading: isLoadingUnit,
    isError: isErrorUnit,
  } = useUnitById(currentUnitId)

  const {
    mutateAsync,
    isPending: isLoadingEditUnit,
    isSuccess: isSuccessEditUnit,
  } = useEditUnitApi()

  const debouncedSearchValue = useDebounce(searchBuilding, 500)

  const {
    data: buildings,
    isLoading: isLoadingBuildings,
    isError,
  } = useSearchBuildingsByText(language, debouncedSearchValue)

  const submitForm: FormikConfig<EditUnitFormState>['onSubmit'] = async values => {
    const { id_deleted_price, translations, building_id, ...restValues } = values
    const updatedTranslations = updateTranslationsForEditing(translations, originalTranslations)
    const hasAiTranslationFlag = hasAiTranslation(updatedTranslations)
    const shouldUpdateTranslations = hasTranslationsChanged(
      updatedTranslations,
      originalTranslations,
    )

    const building_level = buildings?.data?.find(building => building.id === building_id)?.nol ?? 0

    const state = {
      ...restValues,
      building_level,
      building_id,
      has_ai_translation: hasAiTranslationFlag,
      translations: updatedTranslations,
      id_deleted_price: [...deletedPrices],
      shouldUpdateTranslations,
    }

    try {
      await mutateAsync(state)
      onCloseModal()
    } catch (error) {
      errorHandler(error, t('common:notification_messages.edit_unit_error'))
    }
  }

  useEffect(() => {
    if (currentUnit) {
      setInitialValues(() => {
        const {
          id,
          has_ai_translation,
          translations,
          activity,
          default_collection,
          fee,
          fee_local,
          agent_fee,
          agent_fee_local,
          condition,
          type,
          bathrooms,
          bedrooms,
          building,
          level,
          size,
          net_size,
          lotSize,
          hide_price,
          amenities,
          prices,
          hn_hash,
          sn_hash,
          unit_positions,
          agents_comments,
          clients_likes,
          checked_manual,
          emlak_id,
          emlak_code,
        } = currentUnit
        setSearchBuilding(getCurrentTranslation(building, language, 'name'))
        const matchedTranslations = matchTranslations(translations, initialValues.translations)
        setOriginalTranslations({ ...matchedTranslations })

        const transformedPrices = transformPricesFromServer(prices, id)

        return {
          id,
          activity: activity ?? false,
          default_collection: default_collection ?? false,
          fee,
          fee_local: fee_local ?? 0,
          agent_fee: agent_fee ?? 0,
          agent_fee_local: agent_fee_local ?? 0,
          condition,
          type: type ?? null,
          level,
          size,
          net_size,
          lotSize,
          bathrooms,
          bedrooms,
          has_ai_translation,
          translations: matchedTranslations,
          amenities: (amenities || []).map(amenity => amenity.id),
          hide_price: hide_price ?? false,
          building_id: building.id,
          id_deleted_price: [],
          originalPrices: transformedPrices,
          prices: transformedPrices,
          hn_hash,
          sn_hash,
          unit_positions: unit_positions || null,
          agents_comments,
          clients_likes,
          checked_manual,
          emlak_id,
          emlak_code,
        }
      })
    }
  }, [currentUnit])

  useEffect(() => {
    if (isSuccessEditUnit) {
      notification.success(
        t('common:notification_titles.success'),
        t('common:notification_messages.edit_unit_success'),
      )
    }
  }, [isSuccessEditUnit])

  const handleAddPriceForDelete = (data: DeletePriceBody) =>
    setDeletedPrices(preValues => [...preValues, data])

  const amenitiesOptions = createTranslatedSelectOptions(amenities?.data)

  const buildingsOptions = createAutocompleteOptions(buildings?.data, language)

  return {
    initialValues,
    buildings,
    buildingsOptions,
    amenitiesOptions,
    isLoadingBuildings,
    isLoadingEditUnit,
    isLoadingAmenities,
    isLoadingUnit,
    isErrorUnit,
    submitForm,
    handleAddPriceForDelete,
    searchBuilding,
    setSearchBuilding,
    editUnitFormDefault,
  }
}
